/**
 * @fileoverview Edge Function Client with Comprehensive Error Enrichment
 * 
 * This module provides a centralized wrapper for invoking Supabase Edge Functions
 * with rich error context for Sentry. It captures function name, URL, HTTP method,
 * response status, and caller operation context before errors are thrown.
 * 
 * @module lib/edgeFunctionClient
 * 
 * Key Features:
 * - Custom EdgeFunctionError class with full request/response context
 * - Automatic Sentry breadcrumb creation before and after requests
 * - Network error detection and enrichment
 * - Duration tracking for performance monitoring
 * - Caller operation context for tracing back to React Query/hooks
 * 
 * @example Basic usage
 * ```typescript
 * import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
 * 
 * const { data, error } = await invokeEdgeFunction('calculate-tax', {
 *   body: { jobId, items },
 *   callerOperation: 'usePaymentApprovals.calculateTax',
 * });
 * 
 * if (error) throw error; // Throws EdgeFunctionError with full context
 * ```
 */

import { supabase } from '@/integrations/supabase/client';
import { FunctionsHttpError } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';

// Get Supabase URL from environment
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';

// Warn if URL is missing (will result in malformed request URLs in error context)
if (!SUPABASE_URL && typeof window !== 'undefined') {
  console.warn('[EdgeFunctionClient] VITE_SUPABASE_URL not set - request URLs in error context will be incomplete');
}

/**
 * Edge Function error options for constructor
 */
export interface EdgeFunctionErrorOptions {
  functionName: string;
  message: string;
  originalError?: Error;
  httpStatus?: number;
  responseBody?: unknown;
  requestBody?: unknown;
  durationMs: number;
  callerOperation?: string;
}

/**
 * Custom error class for Edge Function failures
 * 
 * Contains full request/response context for debugging:
 * - Function name and URL
 * - HTTP method and status
 * - Request and response bodies (sanitized)
 * - Duration for performance tracking
 * - Caller operation for tracing
 * 
 * @example Accessing error context
 * ```typescript
 * try {
 *   const { data, error } = await invokeEdgeFunction('my-function', { body });
 *   if (error) throw error;
 * } catch (e) {
 *   if (e instanceof EdgeFunctionError) {
 *     console.log(e.functionName, e.httpStatus, e.durationMs);
 *   }
 * }
 * ```
 */
export class EdgeFunctionError extends Error {
  /** Edge function name that was called */
  readonly functionName: string;
  /** Full request URL */
  readonly requestUrl: string;
  /** HTTP method (always POST for Edge Functions) */
  readonly httpMethod: string = 'POST';
  /** HTTP response status code if available */
  readonly httpStatus?: number;
  /** Response body for debugging */
  readonly responseBody?: unknown;
  /** Request body that was sent */
  readonly requestBody?: unknown;
  /** Request duration in milliseconds */
  readonly durationMs: number;
  /** Caller operation (e.g., React Query key, hook name) */
  readonly callerOperation?: string;
  /** Original error if wrapped */
  readonly originalError?: Error;

  constructor(options: EdgeFunctionErrorOptions) {
    // Create descriptive message for better Sentry grouping
    const statusPart = options.httpStatus ? ` (${options.httpStatus})` : '';
    super(`EdgeFunctionError(${options.functionName})${statusPart}: ${options.message}`);
    
    this.name = 'EdgeFunctionError';
    this.functionName = options.functionName;
    this.requestUrl = `${SUPABASE_URL}/functions/v1/${options.functionName}`;
    this.httpStatus = options.httpStatus;
    this.responseBody = options.responseBody;
    this.requestBody = options.requestBody;
    this.durationMs = options.durationMs;
    this.callerOperation = options.callerOperation;
    this.originalError = options.originalError;

    // Preserve original stack if available
    if (options.originalError?.stack) {
      this.stack = `${this.stack}\n\nCaused by:\n${options.originalError.stack}`;
    }
  }

  /**
   * Convert to plain object for logging/serialization
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      functionName: this.functionName,
      requestUrl: this.requestUrl,
      httpMethod: this.httpMethod,
      httpStatus: this.httpStatus,
      durationMs: this.durationMs,
      callerOperation: this.callerOperation,
    };
  }
}

/**
 * Options for invoking an edge function
 */
export interface InvokeOptions {
  /** Request body to send - can be any serializable object */
  body?: Record<string, unknown> | object;
  /** Additional headers */
  headers?: Record<string, string>;
  /** 
   * Caller operation identifier for tracing
   * @example 'usePaymentApprovals.approvePayout'
   * @example 'payment-approvals.calculateTax'
   */
  callerOperation?: string;
}

/**
 * Result type for edge function invocation
 */
export type InvokeResult<T> = 
  | { data: T; error: null }
  | { data: null; error: EdgeFunctionError };

/**
 * Invokes a Supabase Edge Function with comprehensive error enrichment
 * 
 * This wrapper adds rich context to errors for better Sentry debugging:
 * - Adds breadcrumbs before and after the request
 * - Captures HTTP status, response body, and timing
 * - Creates EdgeFunctionError with full context on failure
 * 
 * @param functionName - Name of the edge function to call
 * @param options - Invocation options (body, headers, callerOperation)
 * @returns Promise with data or EdgeFunctionError
 * 
 * @example Success case
 * ```typescript
 * const { data, error } = await invokeEdgeFunction('calculate-tax', {
 *   body: { jobId: '123' },
 *   callerOperation: 'useTaxCalculation.calculate',
 * });
 * if (error) throw error;
 * console.log(data.taxAmount);
 * ```
 * 
 * @example With error handling
 * ```typescript
 * const { data, error } = await invokeEdgeFunction('process-payment', {
 *   body: { amount: 1000 },
 *   callerOperation: 'usePayment.process',
 * });
 * 
 * if (error) {
 *   // error is EdgeFunctionError with full context
 *   console.error(`${error.functionName} failed:`, error.httpStatus);
 *   throw error;
 * }
 * ```
 */
export async function invokeEdgeFunction<T = unknown>(
  functionName: string,
  options: InvokeOptions = {}
): Promise<InvokeResult<T>> {
  const startTime = Date.now();
  const requestUrl = `${SUPABASE_URL}/functions/v1/${functionName}`;

  // Add Sentry breadcrumb BEFORE the request
  Sentry.addBreadcrumb({
    category: 'edge-function',
    message: `Calling ${functionName}`,
    level: 'info',
    data: {
      function_name: functionName,
      request_url: requestUrl,
      http_method: 'POST',
      caller_operation: options.callerOperation,
      request_body_keys: options.body ? Object.keys(options.body) : [],
    },
  });

  try {
    const { data, error } = await supabase.functions.invoke<T>(functionName, {
      body: options.body,
      headers: options.headers,
    });

    const durationMs = Date.now() - startTime;

    if (error) {
      // Extract HTTP status from error if available
      const httpStatus = extractHttpStatus(error);
      
      // Extract actual error message from response body for FunctionsHttpError
      let responseBody: unknown;
      let actualErrorMessage = error.message || 'Edge function request failed';
      
      if (error instanceof FunctionsHttpError) {
        try {
          // The context contains the Response object - parse JSON to get actual error
          responseBody = await error.context.json();
          // Extract the error message from the response body
          if (responseBody && typeof responseBody === 'object') {
            const body = responseBody as Record<string, unknown>;
            if (typeof body.error === 'string') {
              actualErrorMessage = body.error;
            } else if (typeof body.message === 'string') {
              actualErrorMessage = body.message;
            }
          }
        } catch {
          // If JSON parsing fails, try to get text
          try {
            responseBody = await error.context.text();
            if (typeof responseBody === 'string' && responseBody.length < 500) {
              actualErrorMessage = responseBody;
            }
          } catch {
            // Fall back to generic message
            responseBody = extractResponseBody(error);
          }
        }
      } else {
        responseBody = extractResponseBody(error);
      }
      
      const enrichedError = new EdgeFunctionError({
        functionName,
        message: actualErrorMessage,
        originalError: error instanceof Error ? error : undefined,
        httpStatus,
        responseBody,
        requestBody: options.body,
        durationMs,
        callerOperation: options.callerOperation,
      });

      // Add failure breadcrumb with actual error message
      Sentry.addBreadcrumb({
        category: 'edge-function',
        message: `Failed: ${functionName}`,
        level: 'error',
        data: {
          function_name: functionName,
          http_status: httpStatus,
          duration_ms: durationMs,
          error_message: actualErrorMessage,
          caller_operation: options.callerOperation,
        },
      });

      console.error(`[EdgeFunction] ${functionName} failed:`, {
        status: httpStatus,
        message: actualErrorMessage,
        duration: `${durationMs}ms`,
        caller: options.callerOperation,
      });

      return { data: null, error: enrichedError };
    }

    // Add success breadcrumb
    Sentry.addBreadcrumb({
      category: 'edge-function',
      message: `Success: ${functionName}`,
      level: 'info',
      data: {
        function_name: functionName,
        duration_ms: durationMs,
        caller_operation: options.callerOperation,
      },
    });

    return { data: data as T, error: null };
  } catch (networkError) {
    // Handle complete network failures (CORS, timeout, offline)
    const durationMs = Date.now() - startTime;
    const errorMessage = networkError instanceof Error 
      ? networkError.message 
      : 'Network request failed';

    const enrichedError = new EdgeFunctionError({
      functionName,
      message: errorMessage,
      originalError: networkError instanceof Error ? networkError : undefined,
      requestBody: options.body,
      durationMs,
      callerOperation: options.callerOperation,
    });

    // Add network error breadcrumb
    Sentry.addBreadcrumb({
      category: 'edge-function',
      message: `Network error: ${functionName}`,
      level: 'error',
      data: {
        function_name: functionName,
        duration_ms: durationMs,
        error_type: 'network',
        error_message: errorMessage,
        caller_operation: options.callerOperation,
      },
    });

    console.error(`[EdgeFunction] ${functionName} network error:`, {
      message: errorMessage,
      duration: `${durationMs}ms`,
      caller: options.callerOperation,
    });

    return { data: null, error: enrichedError };
  }
}

/**
 * Extracts HTTP status from various error formats
 */
function extractHttpStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  
  const err = error as Record<string, unknown>;
  
  // Direct status property
  if (typeof err.status === 'number') return err.status;
  
  // Nested in context
  if (err.context && typeof err.context === 'object') {
    const ctx = err.context as Record<string, unknown>;
    if (typeof ctx.status === 'number') return ctx.status;
  }
  
  // FunctionsFetchError format
  if (typeof err.code === 'number') return err.code;
  
  return undefined;
}

/**
 * Extracts response body from various error formats
 */
function extractResponseBody(error: unknown): unknown {
  if (!error || typeof error !== 'object') return undefined;
  
  const err = error as Record<string, unknown>;
  
  // Nested in context
  if (err.context && typeof err.context === 'object') {
    const ctx = err.context as Record<string, unknown>;
    if (ctx.body !== undefined) return ctx.body;
    if (ctx.data !== undefined) return ctx.data;
  }
  
  // Direct data property
  if (err.data !== undefined) return err.data;
  
  return undefined;
}

/**
 * Type guard to check if an error is an EdgeFunctionError
 */
export function isEdgeFunctionError(error: unknown): error is EdgeFunctionError {
  return error instanceof EdgeFunctionError || 
    (error !== null && 
     typeof error === 'object' && 
     (error as Error).name === 'EdgeFunctionError');
}
